import { z } from "zod";
import { generateId } from "../../utilities/generate-id";
import type {
    MetaContent} from "../meta-content/meta-content";
import {
    metaContentZodSchema,
} from "../meta-content/meta-content";

type Coordinates = [latitude: number, longitude: number];
const coordinatesZodSchema: z.ZodSchema<Coordinates> = z.tuple([
    z.number(),
    z.number(),
]);
const geoPointZodSchema = z.object({
    type: z.literal("Point"),
    coordinates: coordinatesZodSchema,
});
type GeoPoint = z.infer<typeof geoPointZodSchema>;
const geoLineStringZodSchema = z.object({
    type: z.literal("LineString"),
    coordinates: coordinatesZodSchema.array(),
});
type GeoLineString = z.infer<typeof geoLineStringZodSchema>;
const geoPolygonZodSchema = z.object({
    type: z.literal("Polygon"),
    coordinates: coordinatesZodSchema.array().array(),
});
type GeoPolygon = z.infer<typeof geoPolygonZodSchema>;

type Geometry = GeoPoint | GeoLineString | GeoPolygon;
const geometryZodSchema: z.ZodSchema<Geometry> = z.union([
    geoPointZodSchema,
    geoLineStringZodSchema,
    geoPolygonZodSchema,
]);

const geoJSONZodSchema = z.object({
    type: z.literal("Feature"),
    properties: z.record(z.string(), z.string()),
    geometry: geometryZodSchema,
});
type GeoJSON = z.infer<typeof geoJSONZodSchema>;

const geoLocationZodSchema: z.ZodSchema<GeoLocation> = z.object({
    _id: z.string(),
    location: geoJSONZodSchema,
    meta: metaContentZodSchema,
});
type GeoLocation = {
    readonly _id: string;
    readonly meta: MetaContent;
    readonly location: GeoJSON;
};

const createGeoJSON = (geometry: Geometry): GeoJSON => {
    return {
        type: "Feature",
        properties: {},
        geometry,
    };
};
const createGeoPoint = (coordinates: Coordinates): GeoJSON =>
    createGeoJSON({ type: "Point", coordinates });
const createGeoPolygon = (coordinates: Coordinates[][]): GeoJSON =>
    createGeoJSON({ type: "Polygon", coordinates });
const createGeoLineString = (coordinates: Coordinates[]): GeoJSON =>
    createGeoJSON({ type: "LineString", coordinates });

const createGeoLocation = (
    data: Omit<GeoLocation, "_id"> & { _id?: string },
): GeoLocation => ({
    _id: data._id || generateId({ prefix: "geo_location__" }),
    ...data,
});

const createGeoLocationPoint = (
    data: Omit<GeoLocation, "_id" | "location"> & {
        readonly _id?: string;
        readonly coordinates: Coordinates;
    },
): GeoLocation => ({
    _id: data._id || generateId({ prefix: "geo_location_point__" }),
    location: createGeoPoint(data.coordinates),
    ...data,
});
const createGeoLocationPolygon = (
    data: Omit<GeoLocation, "_id" | "location"> & {
        readonly _id?: string;
        readonly coordinates: Coordinates[][];
    },
): GeoLocation => ({
    _id: data._id || generateId({ prefix: "geo_location_polygon__" }),
    location: createGeoPolygon(data.coordinates),
    ...data,
});
const createGeoLocationLineString = (
    data: Omit<GeoLocation, "_id" | "location"> & {
        readonly _id?: string;
        readonly coordinates: Coordinates[];
    },
): GeoLocation => ({
    _id: data._id || generateId({ prefix: "geo_location_line__" }),
    location: createGeoLineString(data.coordinates),
    ...data,
});

export {
    createGeoLineString,
    createGeoLocation,
    createGeoLocationLineString,
    createGeoLocationPoint,
    createGeoLocationPolygon,
    createGeoPoint,
    createGeoPolygon,
    geoJSONZodSchema,
    geoLineStringZodSchema,
    geoLocationZodSchema,
    geoPointZodSchema,
    geoPolygonZodSchema,
    geometryZodSchema,
};
export type { Coordinates, GeoLocation };
